import type React from 'react'
import { createRef, useEffect, useMemo, useState } from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import useDebounce from 'commons/customHooks/useDebounce'
import { sumArray } from 'commons/utils/array'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import {
  capitalize,
  convertBooleanApiValue,
  decimalStringToNumber,
  isValidDecimalString,
} from 'commons/utils/string'
import {
  type HeadItemType,
  Icon,
  Input,
  Modal,
  NotFound,
  Pagination,
  SlimButton,
  Table,
  Tooltip,
  Typography,
} from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { TypesValidations } from 'constants/enums'
import statusRascunho from 'constants/statusRascunho'
import type { SalesOrderFieldsInterface } from 'container/SalesOrder/interfaces'
import { getSelectedRecords } from 'container/SalesOrder/utils'
import { useFormContext } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { deleteItemRascunho } from 'services/draft'
import { setDefaultParams } from 'store/ProductContext'
import {
  fetchOrderStatistics,
  getItems,
  setDuplicateChecked,
  setMaterialSearch,
  setPendingRecords,
  updateAllItems,
  updateItem,
  updateRecords,
} from 'store/SalesOrderContext/salesOrderSlice'
import type { MaterialsListInterface } from 'store/SalesOrderContext/types/MaterialItemData'
import { setConfirmedNavigation, setShowPrompt } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import type { MaterialsProps } from '..'
import {
  ButtonsRow,
  DivInput,
  EmptySize,
  FactoryWrapper,
  FormActions,
  IconSpan,
  NotFoundContent,
  NotFoundWrapper,
  StrategyText,
  SummaryItem,
  SummaryRow,
  SummaryWrapper,
  TableWrapper,
} from './styles'

const {
  OUT_MINIMUM_ITEM,
  OUT_MINIMUM_ORDER,
  OUT_OF_STOCK,
  WITHOUT_QUOTA,
  INCOMPATIBLE_FACTORY,
  INCOMPLETE_DATA,
  INCONSISTENT_STATUS,
} = statusRascunho

type ColumnDef = {
  id: string
  label: string
  columnName: string
  when: boolean
  render: (r: MaterialsListInterface, key: number) => React.ReactNode
}

function MaterialsList({
  handleRequestMaterials,
  requestMaterials,
  user,
  handleFieldModified,
}: Omit<MaterialsProps, 'setModalMaterialsLimit' | 'hasMaterialsEffected' | 'hasUnsavedOVConfig'>) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { verifyPermission } = usePermissions()

  const {
    records,
    pendingRecords,
    isInternationalOrder,
    isSampleOrder,
    isYTRA,
    isHAVAConectaOrder,
    draftBody: { fl_utlz_fair_shar },
    loadingItemUpdate,
  } = useSelector((state) => state.salesOrder)
  const { defaultParams } = useSelector((state) => state.products)
  const getTotalPages = (recordsLength: number) => Math.ceil(recordsLength / pageIn)
  const pageIn = 20
  const [totalPages, setTotalPages] = useState<number>(getTotalPages(records.length))
  const [currentPage, setCurrentPage] = useState<number>(1)
  const getRecordsKey = (key: number) => (currentPage - 1) * pageIn + (key + 1) - 1
  const [modalDeleteMaterial, setModalDeleteMaterial] = useState<boolean>(false)

  const methods = useFormContext<SalesOrderFieldsInterface>()
  const { setValue } = methods
  const sizesSet = useMemo(() => {
    const memoSet = new Set<string>()

    pendingRecords.forEach(({ tamanhos }) => {
      tamanhos.forEach(({ cd_tamh }) => {
        const t = { size: cd_tamh }
        memoSet.add(JSON.stringify(t))
      })
    })

    return memoSet
  }, [pendingRecords])

  const hasNegativeFeedback = useMemo(
    () => pendingRecords.some((record) => record.ds_moti_repv_anls),
    [pendingRecords],
  )

  const shouldDisable = (action: ActionPerm) => {
    return !verifyPermission(SCREENS.ORDER, action)
  }

  const sizeHeadItems = useMemo<HeadItemType[]>(() => {
    return [...sizesSet].sort().map((size) => {
      const obj: { size: string } = JSON.parse(size)
      return { columnName: obj.size, label: obj.size }
    })
  }, [sizesSet])

  const tableLabels = (): HeadItemType[] => {
    const base: HeadItemType[] = [
      { columnName: '', label: '' },
      { columnName: 'Material', label: 'Material' },
      { columnName: 'DescricaoMaterial', label: 'Descrição' },
      { columnName: 'Cor', label: 'Cor' },
      { columnName: 'DescricaoCor', label: 'Descrição' },
    ]

    const headers: HeadItemType[] = [...base]

    if (!isSampleOrder) {
      headers.push({ columnName: 'Estrategico', label: 'Estratégico' })
    }

    headers.push(
      { columnName: 'Fabrica', label: 'Centro' },
      { columnName: 'Qtd.', label: 'Qtd.' },
      { columnName: 'Valor', label: 'Valor (R$)' },
    )

    if (!isSampleOrder) {
      headers.push({ columnName: 'Desc', label: 'Desc. (%)' })
    }

    if (hasNegativeFeedback && !isSampleOrder) {
      const qtdIdx = headers.findIndex((c: HeadItemType) => c.columnName === 'Qtd.')
      const motivoCol: HeadItemType = {
        columnName: 'MotivoReprovacao',
        label: 'Motivo de reprovação',
      }
      if (qtdIdx >= 0) headers.splice(qtdIdx, 0, motivoCol)
    }

    return [...headers, ...sizeHeadItems]
  }

  const renderQtyBySize = (record: MaterialsListInterface, size: string, key: number) => {
    const foundedSize = record.tamanhos.find((t) => t.cd_tamh === size)
    if (foundedSize?.cd_tamh) {
      return record.cd_matr[0] === '7' // TODO: Replace magic numbers (status) with status enum
        ? numberToDecimalString(foundedSize.qt_item)
        : numberToDecimalString(foundedSize.qt_item_conv)
    }
    return <EmptySize key={`${key}-${size}`}>0</EmptySize>
  }

  const renderDiscountField = (record: MaterialsListInterface, key: number) => {
    const inputRef = createRef<HTMLInputElement>()
    const handleClick = () => {
      inputRef.current!.select()
      inputRef.current!.setSelectionRange(0, inputRef.current!.value.length)
    }

    return (
      <DivInput key={`${record.cd_matr}-${record.cd_cor}-${record.nr_item_pedi_rasc}`}>
        <Input
          disabled={isYTRA || isHAVAConectaOrder || isInternationalOrder}
          height={25}
          InputProps={{
            placeholder: '0',
          }}
          name={`pc_dsct-${record.cd_matr}-${record.cd_cor}-${record.nr_item_pedi_rasc}`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleDiscount(getRecordsKey(key), event)
            handleFieldModified()
          }}
          onClick={handleClick}
          ref={inputRef}
          value={record.pc_dsct.toString().replace('.', ',')} // TODO: Create utils function to replace decimal separator
          width={60}
        />
      </DivInput>
    )
  }

  const isFairShareOffice = convertBooleanApiValue(fl_utlz_fair_shar)

  const columnDefs = useMemo<ColumnDef[]>(
    () => [
      {
        columnName: '',
        id: 'icon',
        label: '',
        render: (record: MaterialsListInterface) => (
          <Tooltip
            arrow
            key={`icon-${record.cd_matr}-${record.cd_cor}`}
            placement="top"
            title={getTooltipLabel(record, isFairShareOffice)}
          >
            <IconSpan>{getTooltipIcon(record)}</IconSpan>
          </Tooltip>
        ),
        when: true,
      },
      {
        columnName: 'Material',
        id: 'material',
        label: 'Material',
        render: (r: MaterialsListInterface) => r.cd_matr,
        when: true,
      },
      {
        columnName: 'DescricaoMaterial',
        id: 'descricao-material',
        label: 'Descrição',
        render: (r: MaterialsListInterface) => capitalize(r.ds_matr),
        when: true,
      },
      {
        columnName: 'Cor',
        id: 'cor',
        label: 'Cor',
        render: (r: MaterialsListInterface) => r.cd_cor,
        when: true,
      },
      {
        columnName: 'DescricaoCor',
        id: 'descricao-cor',
        label: 'Descrição',
        render: (r: MaterialsListInterface) => capitalize(r.ds_cor),
        when: true,
      },
      {
        columnName: 'Estrategico',
        id: 'estrategico',
        label: 'Estratégico',
        render: (r: MaterialsListInterface) =>
          r.fl_matr_estt === TypesValidations.YES ? <StrategyText>Sim</StrategyText> : 'Não',
        when: !isSampleOrder,
      },
      {
        columnName: 'Fabrica',
        id: 'centro',
        label: 'Centro',
        render: (r: MaterialsListInterface, key: number) => (
          <FactoryWrapper key={`${r.cd_cent_matr}-${key}`}>{r.cd_cent_matr ?? '--'}</FactoryWrapper>
        ),
        when: true,
      },
      {
        columnName: 'MotivoReprovacao',
        id: 'motivo-reprovacao',
        label: 'Motivo de reprovação',
        render: (r: MaterialsListInterface) => r.ds_moti_repv_anls ?? '-',
        when: hasNegativeFeedback && !isSampleOrder,
      },
      {
        columnName: 'Qtd.',
        id: 'qtd',
        label: 'Qtd.',
        render: (r: MaterialsListInterface) => numberToDecimalString(r.qt_tota_item_conv),
        when: true,
      },
      {
        columnName: 'Valor',
        id: 'valor',
        label: 'Valor (R$)',
        render: (r: MaterialsListInterface) =>
          r.vl_tota_item ? formatToPTBRNumber(r.vl_tota_item) : '-',
        when: true,
      },
      {
        columnName: 'Desc',
        id: 'desc',
        label: 'Desc. (%)',
        render: (r: MaterialsListInterface, key: number) => renderDiscountField(r, key),
        when: !isSampleOrder,
      },
    ],
    [isSampleOrder, hasNegativeFeedback, currentPage, pendingRecords],
  )

  const sizeColumnDefs = useMemo<ColumnDef[]>(() => {
    return [...sizesSet].sort().map((size) => {
      const obj: { size: string } = JSON.parse(size)
      const id = `size-${obj.size}`
      return {
        columnName: obj.size,
        id,
        label: obj.size,
        render: (r: MaterialsListInterface, key: number) => renderQtyBySize(r, obj.size, key),
        when: true,
      }
    })
  }, [sizesSet])

  const getRecordsDataUnified = () => {
    return pendingRecords
      .slice(pageIn * (currentPage - 1), pageIn * currentPage)
      .map((record, key) => {
        const baseContent = columnDefs.filter((c) => c.when).map((c) => c.render(record, key))

        const sizeContent = sizeColumnDefs.map((c) => c.render(record, key))

        return {
          checked: convertBooleanApiValue(record.fl_sele),
          content: [...baseContent, ...sizeContent],
          id: `${record.cd_matr}-${record.cd_cor}-${record.nr_item_pedi_rasc}`,
          onDoubleClick: () => {
            dispatch(setConfirmedNavigation(true))
            dispatch(
              setDefaultParams({
                ...defaultParams,
                cd_cent_matr: record.cd_cent_matr,
                nr_item_pedi_rasc: record.nr_item_pedi_rasc,
              }),
            )
            navigate(`${pathname}/${record.cd_matr}`)
            dispatch(setShowPrompt(false))
          },
        }
      })
  }

  const updateMaterialDiscount = (id: string, discountValue: string) => {
    const [ds_matr, cd_cor, nr_item_pedi_rasc] = id.split('-')

    let material = null

    records.forEach((record) => {
      if (
        record.cd_matr === ds_matr &&
        record.cd_cor === cd_cor &&
        record.nr_item_pedi_rasc === +nr_item_pedi_rasc
      ) {
        material = { ...record, pc_dsct: discountValue }
      }
    })

    if (material !== null)
      dispatch(
        updateItem(material, () => {
          toast.success('Desconto alterado com sucesso.')
          dispatch(fetchOrderStatistics(defaultParams.nr_pedi_rasc as string))
          dispatch(getItems(''))
          // TODO: Add improvement: Keep in the same page (pagination) after update
        }),
      )
  }
  const debounceChange = useDebounce(updateMaterialDiscount, 1000)

  const handleDiscountValue = (currentValue: string, value: string): string => {
    const newValue = value.replace('.', ',') // TODO: Create utils function to replace decimal separator

    if (value === '') return '0'
    if (!isValidDecimalString(newValue)) return currentValue

    const valueAsNumber = decimalStringToNumber(newValue)

    if (valueAsNumber > 99.99) {
      return '99,99'
    } else if (valueAsNumber < 0) {
      return '0'
    } else {
      if (newValue.includes(',')) return newValue
      else return valueAsNumber.toString()
    }
  }

  const calcBox = () => {
    const totalBox = pendingRecords.map((item) => {
      return item.qt_tota_item
    })
    return totalBox.reduce(sumArray, 0)
  }

  const calcPars = () => {
    const totalPairs = pendingRecords.map((item) => {
      return item.qt_tota_item_conv
    })
    return totalPairs.reduce(sumArray, 0)
  }

  const calcSelectedPairs = () => {
    const valuesToSum = getSelectedRecords(records).map((item) => {
      return item.qt_tota_item_conv
    })
    return numberToDecimalString(valuesToSum.reduce(sumArray, 0))
  }

  const calcTotal = () => {
    const valuesToSum = pendingRecords.map((item) => item.vl_tota_item || 0)
    return valuesToSum.reduce(sumArray, 0)
  }

  const handleDiscount = (key: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const pending = pendingRecords
    if (
      event.target.name !==
      `pc_dsct-${pending[key].cd_matr}-${pending[key].cd_cor}-${pending[key].nr_item_pedi_rasc}`
    ) {
      return
    }

    const currentValue = pending[key]?.pc_dsct.toString()
    const newValue = handleDiscountValue(currentValue, event.target.value)

    if (currentValue !== newValue) {
      const newRecords = pending.map((record) => {
        if (
          pending[key].cd_matr === record.cd_matr &&
          pending[key].nr_item_pedi_rasc === record.nr_item_pedi_rasc
        ) {
          return {
            ...record,
            pc_dsct: newValue,
          }
        }
        return record
      })

      dispatch(setPendingRecords(newRecords))
      dispatch(setDuplicateChecked([false]))
      setValue('fl_dsct_matr', TypesValidations.NO)
      debounceChange(
        `${pending[key].cd_matr}-${pending[key].cd_cor}-${pending[key].nr_item_pedi_rasc}`,
        newValue,
      )
    }
  }

  useEffect(() => {
    setTotalPages(getTotalPages(pendingRecords.length))

    const getNewCurrentPage = () => {
      const amountOfPages = Math.ceil(pendingRecords.length / pageIn)
      const realAmountOfPages = amountOfPages < 1 ? 1 : amountOfPages

      return currentPage < realAmountOfPages ? currentPage : realAmountOfPages
    }
    setCurrentPage(getNewCurrentPage())
  }, [records])

  useEffect(() => {
    dispatch(setConfirmedNavigation(false))
  }, [])

  const handleDelete = () => {
    const recordsToDelete = getSelectedRecords(records).map((record) => ({
      cd_cor: record?.cd_cor,
      nr_item_pedi_rasc: record?.nr_item_pedi_rasc,
    }))

    const body = {
      cd_usua_alte: user,
      ds_item_pedi_rasc: recordsToDelete,
      nr_pedi_rasc: defaultParams.nr_pedi_rasc as string,
    }

    deleteItemRascunho(body).then((data) => {
      if (!data) return

      dispatch(getItems(''))
      setModalDeleteMaterial(false)
    })
  }

  const clearMaterialSearch = () => {
    dispatch(setMaterialSearch(''))
    dispatch(getItems())
  }

  const renderFooter = () => {
    return (
      <SummaryWrapper>
        <div>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              itemsPerPage={pageIn}
              numberOfItems={pendingRecords.length}
              numberOfPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          )}
        </div>
        <SummaryRow>
          <SummaryItem>
            <span>Volume de pares</span>
            <Typography design="hav-default-03-text">
              {numberToDecimalString(calcPars())}
            </Typography>
          </SummaryItem>
          <SummaryItem>
            <span>Volume de caixas</span>
            <Typography design="hav-default-03-text">{numberToDecimalString(calcBox())}</Typography>
          </SummaryItem>
          <SummaryItem>
            <span>Valor total</span>
            <Typography design="hav-default-03-text">{formatToPTBRNumber(calcTotal())}</Typography>
          </SummaryItem>
        </SummaryRow>
      </SummaryWrapper>
    )
  }

  const getTooltipLabel = (record: MaterialsListInterface, isFairShare: boolean) => {
    if (record.cd_stat_pedi_rasc_item === 20) return 'Volume extra aprovado'
    if (record.ds_moti_repv_anls) return 'Volume extra reprovado'
    if (record.ds_moti_crit === OUT_MINIMUM_ITEM) return 'Indisponível por volume mínimo'
    if (record.ds_moti_crit === OUT_MINIMUM_ORDER) return 'Indisponível por valor mínimo'
    if (record.ds_moti_crit === OUT_OF_STOCK) return 'Indisponível por estoque'
    if (record.ds_moti_crit === WITHOUT_QUOTA)
      return isFairShare ? 'Sem disponibilidade' : 'Indisponível por cota'
    if (record.ds_moti_crit === INCOMPATIBLE_FACTORY)
      return 'Fábrica inválida, edite o material e verifique as fábricas disponíveis'
    if (record.ds_moti_crit === INCOMPLETE_DATA)
      return 'Cadastro incompleto, para mais informações consulte o backoffice'
    if (record.ds_moti_crit === INCONSISTENT_STATUS) return 'Inconsistência de status de material'
    return 'Rascunho'
  }

  const getTooltipIcon = (record: MaterialsListInterface) => {
    const ICON_SIZE = '15'

    // TODO: Replace magic numbers (status) with status enum
    if (record.cd_stat_pedi_rasc_item === 20)
      return <Icon color={theme.colors.alert.success.dark} icon="task-alt" size={ICON_SIZE} />
    if (record.ds_moti_repv_anls)
      return <Icon color={theme.colors.alert.danger.dark} icon="cancel" size={ICON_SIZE} />
    if (record.ds_moti_crit === OUT_MINIMUM_ITEM)
      return <Icon color={theme.colors.alert.danger.dark} icon="sandal" size={ICON_SIZE} />
    if (record.ds_moti_crit === OUT_MINIMUM_ORDER)
      return <Icon color={theme.colors.alert.danger.dark} icon="money" size={ICON_SIZE} />
    if (record.ds_moti_crit === OUT_OF_STOCK)
      return <Icon color={theme.colors.alert.warning.dark} icon="box" size={ICON_SIZE} />
    if (record.ds_moti_crit === WITHOUT_QUOTA)
      return <Icon color={theme.colors.alert.warning.dark} icon="report" size={ICON_SIZE} />
    if (record.ds_moti_crit === INCOMPATIBLE_FACTORY)
      return <Icon color={theme.colors.alert.warning.dark} icon="factory" size={ICON_SIZE} />
    if (record.ds_moti_crit === INCOMPLETE_DATA)
      return <Icon color={theme.colors.alert.danger.dark} icon="missing-piece" size={ICON_SIZE} />
    if (record.ds_moti_crit === INCONSISTENT_STATUS)
      return <Icon color={theme.colors.alert.danger.dark} icon="swap" size={ICON_SIZE} />

    return <Icon color={theme.colors.neutral.light} icon="edit" size={ICON_SIZE} />
  }

  const actionRow = () => {
    return (
      <FormActions>
        <ButtonsRow>
          <SlimButton
            content="Solicitar"
            disabled={
              getSelectedRecords(records).length === 0 || shouldDisable(ACTIONS_SCREENS.REQUEST)
            }
            icon={<Icon icon="send" size="15" />}
            id="material-request-button"
            loading={requestMaterials.loading}
            onClick={() => handleRequestMaterials()}
            type="button"
            variant="primary"
          />
          <SlimButton
            content="Excluir"
            disabled={
              getSelectedRecords(records).length === 0 || shouldDisable(ACTIONS_SCREENS.EDIT)
            }
            icon={<Icon icon="trash" size="15" />}
            id="material-delete-button"
            loading={requestMaterials.loading}
            onClick={() => setModalDeleteMaterial(true)}
            type="button"
            variant="secondary"
          />
        </ButtonsRow>
        <Typography design="hav-default-03-text">
          {getSelectedRecords(records).length > 0
            ? `${calcSelectedPairs()} pares selecionados`
            : 'Nenhum par selecionado'}
        </Typography>
      </FormActions>
    )
  }

  function updateCheck(rec: MaterialsListInterface[], code?: string[]) {
    const newRecords: MaterialsListInterface[] = rec.map((record) => {
      if (code && code.includes(record.cd_matr)) {
        return {
          ...record,
          fl_sele: TypesValidations.NO,
        }
      }
      return record
    })

    dispatch(updateRecords(newRecords))
  }

  const setMaterialSelection = (checked: boolean, id?: string) => {
    if (id) {
      const [ds_matr, cd_cor, nr_item_pedi_rasc] = id.split('-')

      let material = null

      const newRecords: MaterialsListInterface[] = records.map((record) => {
        if (
          record.cd_matr === ds_matr &&
          record.cd_cor === cd_cor &&
          record.nr_item_pedi_rasc === Number(nr_item_pedi_rasc)
        ) {
          material = {
            ...record,
            fl_sele: checked ? TypesValidations.YES : TypesValidations.NO,
          }
          return {
            ...record,
            fl_sele: checked ? TypesValidations.YES : TypesValidations.NO,
          }
        }
        return record
      })
      if (material !== null) dispatch(updateItem(material, (code) => updateCheck(newRecords, code)))
    } else {
      const newRecords: MaterialsListInterface[] = records.map((record) => {
        // TODO: Replace magic numbers (status) with status enum
        if (record.cd_stat_pedi_rasc_item === 0 || record.cd_stat_pedi_rasc_item === 20) {
          return {
            ...record,
            fl_sele: checked ? TypesValidations.YES : TypesValidations.NO,
          }
        }
        return record
      })
      dispatch(
        updateAllItems(
          (code) => updateCheck(newRecords, code),
          checked ? TypesValidations.YES : TypesValidations.NO,
          true,
        ),
      )
    }
  }

  return (
    <>
      {pendingRecords.length ? (
        <>
          {actionRow()}
          <TableWrapper>
            <Table
              bodyData={getRecordsDataUnified()}
              checkboxSize="small"
              hasCheckbox
              headData={tableLabels()}
              iconColumns={[0]}
              loadingChecked={loadingItemUpdate}
              rowHeight={32}
              setChecked={setMaterialSelection}
            />
          </TableWrapper>
        </>
      ) : (
        <NotFoundWrapper>
          <NotFound
            description={
              <NotFoundContent>
                Verifique os critérios utilizados e tente novamente.
                <SlimButton
                  content="Reiniciar busca e filtros"
                  icon={<Icon icon="chevron-left" size="15" />}
                  onClick={clearMaterialSearch}
                  type="button"
                  variant="white"
                />
              </NotFoundContent>
            }
            title={'Nenhum resultado encontrado'}
          />
        </NotFoundWrapper>
      )}
      {renderFooter()}

      <Modal
        buttonAction={handleDelete}
        buttonIcon={<Icon icon="trash" size="15" />}
        buttonText="Excluir materiais"
        description="Após excluir os materiais, você não poderá editá-los novamente. Deseja mesmo excluir?"
        open={modalDeleteMaterial}
        returnText="Cancelar"
        setOpen={setModalDeleteMaterial}
        showReturn
        title="Excluir Materiais Selecionados"
      />
    </>
  )
}

export default MaterialsList
